import { UnitTypeName } from '../types/unitListTypes';

export interface UnitsListFilterState {
  buildingIds: string[];
  floorIds: string[];
  sortColumnName?: string;
  sortDescending?: boolean;
  searchTerm?: string;
}

export interface ResolvedUnitsListFilters {
  buildingIds: string[];
  floorIds: string[];
  sortColumnName: string;
  sortDescending: boolean;
  searchTerm: string;
  unitTypeName?: UnitTypeName;
  restoredFromStorage: boolean;
  hasActiveLocationFilters: boolean;
}

interface SelectableEntity {
  _id?: string | null;
  name?: string | null;
  number?: number | null;
}

interface SelectableFloor extends SelectableEntity {
  buildingId?: string | null;
}

interface SelectableUnit {
  type?: string | null;
  buildingId?: string | null;
  floorId?: string | null;
}

interface ResolveInitialUnitListFiltersArgs {
  buildings?: readonly (SelectableEntity | null | undefined)[];
  floors?: readonly (SelectableFloor | null | undefined)[];
  units?: readonly (SelectableUnit | null | undefined)[];
  currentBuildingIds?: string[] | null;
  currentFloorIds?: string[] | null;
  currentUnitType?: UnitTypeName | null;
  currentSearchTerm?: string | null;
  currentSortBy?: string | null;
  currentSortDesc?: string | null;
  storedFilters?: UnitsListFilterState | null;
}

const preferredUnitTypeOrder: UnitTypeName[] = [
  'residential',
  'commonArea',
  'centralEquipment',
];

function byNumberThenName<T extends SelectableEntity>(a: T, b: T): number {
  return (
    (a.number || 0) - (b.number || 0) ||
    (a.name || '').localeCompare(b.name || '') ||
    0
  );
}

function withIds<T extends SelectableEntity>(
  items: readonly (T | null | undefined)[] = [],
): (T & { _id: string })[] {
  return items.filter((item): item is T & { _id: string } => !!item?._id);
}

function validIds(ids: string[] | null | undefined, valid: Set<string>) {
  return ids?.filter((id) => valid.has(id)) ?? [];
}

function hasStoredSelection(ids: string[] | undefined): ids is string[] {
  return Array.isArray(ids) && ids.length > 0;
}

function isUnitTypeName(
  value: string | null | undefined,
): value is UnitTypeName {
  return preferredUnitTypeOrder.includes(value as UnitTypeName);
}

function availableUnitTypes(units: readonly SelectableUnit[]): UnitTypeName[] {
  return preferredUnitTypeOrder.filter((unitTypeName) =>
    units.some((unit) => unit.type === unitTypeName),
  );
}

function floorsForBuildings(
  floors: (SelectableFloor & { _id: string })[],
  buildingIds: string[],
): (SelectableFloor & { _id: string })[] {
  return buildingIds.length > 0
    ? floors.filter((floor) => buildingIds.includes(floor.buildingId || ''))
    : floors;
}

function unitMatchesFilters(
  unit: SelectableUnit,
  unitTypeName: UnitTypeName | undefined,
  buildingIds: string[],
  floorIds: string[],
): boolean {
  if (!unitTypeName || unit.type !== unitTypeName) return false;
  if (!unit.buildingId || !buildingIds.includes(unit.buildingId)) return false;
  if (!unit.floorId) return true;
  return floorIds.includes(unit.floorId);
}

function hasMatchingUnit(
  units: readonly SelectableUnit[],
  unitTypeName: UnitTypeName | undefined,
  buildingIds: string[],
  floorIds: string[],
): boolean {
  return units.some((unit) =>
    unitMatchesFilters(unit, unitTypeName, buildingIds, floorIds),
  );
}

function resolveUnitTypeName(
  units: readonly SelectableUnit[],
  currentUnitType: UnitTypeName | null,
): UnitTypeName | undefined {
  const availableTypes = availableUnitTypes(units);
  if (currentUnitType && availableTypes.includes(currentUnitType)) {
    return currentUnitType;
  }
  return availableTypes[0];
}

function unitTypeCandidates(
  units: readonly SelectableUnit[],
  selectedUnitTypeName: UnitTypeName | undefined,
  unitTypeLocked: boolean,
): UnitTypeName[] {
  if (!selectedUnitTypeName) return [];
  if (unitTypeLocked) return [selectedUnitTypeName];

  return [
    selectedUnitTypeName,
    ...availableUnitTypes(units).filter(
      (unitTypeName) => unitTypeName !== selectedUnitTypeName,
    ),
  ];
}

function hasActiveLocationFilters(
  sortedBuildings: (SelectableEntity & { _id: string })[],
  sortedFloors: (SelectableFloor & { _id: string })[],
  buildingIds: string[],
  floorIds: string[],
): boolean {
  const matchingFloors = floorsForBuildings(sortedFloors, buildingIds);
  const buildingFilterActive =
    sortedBuildings.length > 1 && buildingIds.length !== sortedBuildings.length;
  const floorFilterActive =
    matchingFloors.length > 1 && floorIds.length !== matchingFloors.length;

  return buildingFilterActive || floorFilterActive;
}

function resolveNonEmptyDefaults({
  units,
  sortedBuildings,
  sortedFloors,
  selectedUnitTypeName,
  buildingIds,
  floorIds,
  unitTypeLocked,
  buildingIdsLocked,
  floorIdsLocked,
}: {
  units: readonly SelectableUnit[];
  sortedBuildings: (SelectableEntity & { _id: string })[];
  sortedFloors: (SelectableFloor & { _id: string })[];
  selectedUnitTypeName: UnitTypeName | undefined;
  buildingIds: string[];
  floorIds: string[];
  unitTypeLocked: boolean;
  buildingIdsLocked: boolean;
  floorIdsLocked: boolean;
}): {
  unitTypeName: UnitTypeName | undefined;
  buildingIds: string[];
  floorIds: string[];
} {
  if (
    units.length === 0 ||
    hasMatchingUnit(units, selectedUnitTypeName, buildingIds, floorIds)
  ) {
    return { unitTypeName: selectedUnitTypeName, buildingIds, floorIds };
  }

  const buildingCandidates = buildingIdsLocked
    ? [buildingIds]
    : sortedBuildings.length > 0
      ? sortedBuildings.map((building) => [building._id])
      : [buildingIds];

  for (const unitTypeName of unitTypeCandidates(
    units,
    selectedUnitTypeName,
    unitTypeLocked,
  )) {
    for (const candidateBuildingIds of buildingCandidates) {
      const floorCandidates = floorIdsLocked
        ? [floorIds]
        : (() => {
            const candidateFloors = floorsForBuildings(
              sortedFloors,
              candidateBuildingIds,
            );
            return candidateFloors.length > 0
              ? candidateFloors.map((floor) => [floor._id])
              : [[]];
          })();

      for (const candidateFloorIds of floorCandidates) {
        if (
          hasMatchingUnit(
            units,
            unitTypeName,
            candidateBuildingIds,
            candidateFloorIds,
          )
        ) {
          return {
            unitTypeName,
            buildingIds: candidateBuildingIds,
            floorIds: candidateFloorIds,
          };
        }
      }
    }
  }

  return { unitTypeName: selectedUnitTypeName, buildingIds, floorIds };
}

export function resolveInitialUnitListFilters({
  buildings = [],
  floors = [],
  units = [],
  currentBuildingIds = null,
  currentFloorIds = null,
  currentUnitType = null,
  currentSearchTerm = null,
  currentSortBy = null,
  currentSortDesc = null,
  storedFilters = null,
}: ResolveInitialUnitListFiltersArgs): ResolvedUnitsListFilters {
  const sortedBuildings = withIds(buildings).sort(byNumberThenName);
  const sortedFloors = withIds(floors).sort(byNumberThenName);
  const unitsWithLocation = units.filter(
    (unit): unit is SelectableUnit => !!unit,
  );
  const validBuildingIds = new Set(
    sortedBuildings.map((building) => building._id),
  );
  const defaultBuildingIds = sortedBuildings[0]?._id
    ? [sortedBuildings[0]._id]
    : [];

  let restoredFromStorage = false;
  let buildingIds: string[];
  let buildingIdsLocked = false;
  const storedBuildingIds = storedFilters?.buildingIds;

  if (currentBuildingIds) {
    buildingIds = validIds(currentBuildingIds, validBuildingIds);
    buildingIdsLocked = true;
  } else if (hasStoredSelection(storedBuildingIds)) {
    const validStoredBuildingIds = validIds(
      storedBuildingIds,
      validBuildingIds,
    );
    if (validStoredBuildingIds.length > 0) {
      buildingIds = validStoredBuildingIds;
      restoredFromStorage = true;
      buildingIdsLocked = true;
    } else {
      buildingIds = defaultBuildingIds;
    }
  } else {
    buildingIds = defaultBuildingIds;
  }

  const candidateFloors = floorsForBuildings(sortedFloors, buildingIds);
  const validFloorIds = new Set(candidateFloors.map((floor) => floor._id));
  const defaultFloorIds = candidateFloors[0]?._id
    ? [candidateFloors[0]._id]
    : [];

  let floorIds: string[];
  let floorIdsLocked = false;
  const storedFloorIds = storedFilters?.floorIds;

  if (currentFloorIds) {
    floorIds = validIds(currentFloorIds, validFloorIds);
    floorIdsLocked = true;
  } else if (hasStoredSelection(storedFloorIds)) {
    const validStoredFloorIds = validIds(storedFloorIds, validFloorIds);
    if (validStoredFloorIds.length > 0) {
      floorIds = validStoredFloorIds;
      restoredFromStorage = true;
      floorIdsLocked = true;
    } else {
      floorIds = defaultFloorIds;
    }
  } else {
    floorIds = defaultFloorIds;
  }

  const selectedUnitTypeName = resolveUnitTypeName(
    unitsWithLocation,
    currentUnitType,
  );
  const unitTypeLocked =
    currentUnitType !== null &&
    isUnitTypeName(currentUnitType) &&
    selectedUnitTypeName === currentUnitType;
  const nonEmptyDefaults = resolveNonEmptyDefaults({
    units: unitsWithLocation,
    sortedBuildings,
    sortedFloors,
    selectedUnitTypeName,
    buildingIds,
    floorIds,
    unitTypeLocked,
    buildingIdsLocked,
    floorIdsLocked,
  });

  buildingIds = nonEmptyDefaults.buildingIds;
  floorIds = nonEmptyDefaults.floorIds;

  let sortColumnName: string;
  if (currentSortBy) {
    sortColumnName = currentSortBy;
  } else {
    sortColumnName = storedFilters?.sortColumnName ?? 'name';
    if (storedFilters?.sortColumnName) {
      restoredFromStorage = true;
    }
  }

  let sortDescending: boolean;
  if (currentSortDesc !== null) {
    sortDescending = currentSortDesc === 'true';
  } else {
    sortDescending = storedFilters?.sortDescending ?? false;
    if (storedFilters?.sortDescending !== undefined) {
      restoredFromStorage = true;
    }
  }

  let searchTerm: string;
  if (currentSearchTerm !== null) {
    searchTerm = currentSearchTerm;
  } else {
    searchTerm = storedFilters?.searchTerm ?? '';
    if (storedFilters?.searchTerm) {
      restoredFromStorage = true;
    }
  }

  return {
    buildingIds,
    floorIds,
    sortColumnName,
    sortDescending,
    searchTerm,
    unitTypeName: nonEmptyDefaults.unitTypeName,
    restoredFromStorage,
    hasActiveLocationFilters: hasActiveLocationFilters(
      sortedBuildings,
      sortedFloors,
      buildingIds,
      floorIds,
    ),
  };
}
